/**
 * ForecastTimelineInset Component
 *
 * A draggable inset component that displays forecast timeline data on the map.
 * Shows how the forecast evolves over multiple days with confidence bands.
 */

import * as React from "react";
import AbstractInset, { BaseInsetProps, InsetState } from "./AbstractInset";
import {
  ForecastEvolutionChart,
  ForecastTimelineChart,
  createEvolutionLineConfigs,
} from "@/components/charts";
import { useForecastRange, useForecastTrends } from "@/hooks/api";
import type { StormImpactModel } from "@/types";
import { format } from "date-fns";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  VariableSelectionDropdown,
  type VariableOption,
} from "./VariableSelectionDropdown";
import { AreaChart, GitBranch, Info } from "lucide-react";

const DEFAULT_EVOLUTION_VARIABLES = [
  "estimate90th",
  "predicted_median",
  "estimate75th",
  "best_estimate",
];

export interface ForecastTimelineInsetProps extends Omit<
  BaseInsetProps,
  "data" | "headers" | "areaNameKey"
> {
  /** Geographic node ID */
  nodeId?: string;
  /** Storm impact model ID */
  modelId?: string;
  /** Weather model ID */
  weatherModelId?: string;
  /** Start date for the forecast */
  startDate?: Date;
  /** Number of days to forecast */
  forecastRange?: number;
  /** Model run timestamp (ISO string) */
  run?: string;
  /** From available-runs — Evolution uses these as the run source of truth. */
  availableRuns?: string[];
  availableRunsLoading?: boolean;
  /**
   * When false, Evolution waits for primary panels (map/timeline/grid).
   * Defaults to true when omitted.
   */
  evolutionEnabled?: boolean;
  /** Leaf nodes under selected geo scope — scales Evolution fan-out. */
  scopeLeafCount?: number;
  /** Storm impact model configuration */
  stormImpactModel?: StormImpactModel;
  /** Y-axis label/units */
  units?: string;
  /** Whether the inset is visible */
  visible?: boolean;
  /** Default selected tab */
  defaultTab?: "timeline" | "evolution";
}

/**
 * ForecastTimelineInset Component
 *
 * Displays a forecast timeline chart in a draggable, collapsible inset.
 * Fetches data via the useForecastRange hook and renders using ForecastTimelineChart.
 */
const ForecastTimelineInset: React.FC<ForecastTimelineInsetProps> = ({
  nodeId,
  modelId,
  weatherModelId,
  startDate,
  forecastRange = 5,
  run,
  availableRuns,
  availableRunsLoading = false,
  evolutionEnabled = true,
  scopeLeafCount = 0,
  stormImpactModel,
  units = "Customers",
  visible = true,
  defaultTab = "timeline",
  position = "bottom-right",
  width = "450px",
  height = "380px",
  title = "Forecast Timeline",
  subtitle,
  isCollapsible = true,
  initiallyCollapsed = false,
  className,
  isDraggable = true,
  onPositionChange,
  panelMode = false,
}) => {
  const [activeTab, setActiveTab] = React.useState<"timeline" | "evolution">(
    defaultTab,
  );

  const [selectedEvolutionVariables, setSelectedEvolutionVariables] =
    React.useState<string[]>(DEFAULT_EVOLUTION_VARIABLES);

  // Track whether we've ever had valid params so we can keep rendering stale data
  // during parameter transitions (e.g. date change clears selectedRun temporarily)
  const hasEverHadDataRef = React.useRef(false);
  const allParamsPresent =
    !!nodeId && !!modelId && !!weatherModelId && !!startDate && !!run;
  const isTransitioning = hasEverHadDataRef.current && !allParamsPresent;

  // Fetch forecast data using the hook
  // With placeholderData: keepPreviousData, stale data persists while new query loads
  const { data, loading, error, isPlaceholderData } = useForecastRange({
    nodeId,
    modelId,
    weatherModelId,
    startDate,
    forecastRange,
    run,
    stormImpactModel,
  });

  // Mark that we've had data at least once
  if (data.length > 0) {
    hasEverHadDataRef.current = true;
  }

  // Treat as "loading" when params are transitioning (e.g. run cleared during date switch)
  // or when we're still showing placeholder (previous) data - so we never flash old chart
  const isRefreshing = loading || isTransitioning || isPlaceholderData;

  const waitingForParams = !allParamsPresent && !hasEverHadDataRef.current;

  const forecastDate = React.useMemo(
    () => (startDate ? format(startDate, "yyyy-MM-dd") : undefined),
    [startDate],
  );

  const {
    data: evolutionData,
    loading: evolutionLoading,
    error: evolutionError,
    availableVariables: evolutionAvailableVariables,
  } = useForecastTrends({
    nodeId,
    modelId,
    weatherModelId,
    forecastDate,
    availableRuns,
    availableRunsLoading,
    stormImpactModel,
    scopeLeafCount,
    // Tab lazy-load + defer until primary run/grid path is ready.
    enabled:
      activeTab === "evolution" && evolutionEnabled && !!run && !availableRunsLoading,
  });

  const evolutionParamsPresent =
    !!nodeId && !!modelId && !!weatherModelId && !!forecastDate;
  const hasEverHadEvolutionDataRef = React.useRef(false);
  if (evolutionData.length > 0) {
    hasEverHadEvolutionDataRef.current = true;
  }
  const isEvolutionDeferred =
    activeTab === "evolution" &&
    evolutionParamsPresent &&
    (!evolutionEnabled || !run || availableRunsLoading);
  const isEvolutionTransitioning =
    hasEverHadEvolutionDataRef.current && !evolutionParamsPresent;
  const isEvolutionRefreshing =
    evolutionLoading || isEvolutionTransitioning || isEvolutionDeferred;

  const availableEvolutionVariables = React.useMemo<VariableOption[]>(() => {
    return evolutionAvailableVariables.map((slug) => {
      const variable = stormImpactModel?.variables?.find(
        (v) => v.variable_slug === slug,
      );
      return {
        id: slug,
        slug,
        name:
          variable?.variable_name ||
          slug.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()),
        units: variable?.variable_units ?? null,
        order: variable?.variable_order ?? 999,
        variableType: variable?.variable_type,
      };
    });
  }, [evolutionAvailableVariables, stormImpactModel?.variables]);

  React.useEffect(() => {
    if (availableEvolutionVariables.length === 0) return;
    const validSelected = selectedEvolutionVariables.filter((v) =>
      evolutionAvailableVariables.includes(v),
    );
    if (validSelected.length === 0) {
      const defaults = DEFAULT_EVOLUTION_VARIABLES.filter((v) =>
        evolutionAvailableVariables.includes(v),
      );
      setSelectedEvolutionVariables(
        defaults.length > 0 ? defaults : [evolutionAvailableVariables[0]],
      );
    } else if (validSelected.length !== selectedEvolutionVariables.length) {
      setSelectedEvolutionVariables(validSelected);
    }
  }, [evolutionAvailableVariables, availableEvolutionVariables.length, selectedEvolutionVariables]);

  const handleEvolutionVariablesChange = React.useCallback(
    (newSelection: string[]) => {
      if (newSelection.length === 0) {
        setSelectedEvolutionVariables([]);
        return;
      }
      const prev = selectedEvolutionVariables;
      const added = newSelection.filter((v) => !prev.includes(v));
      if (added.length === 0) {
        setSelectedEvolutionVariables(newSelection);
        return;
      }
      const getUnits = (slug: string) => {
        const v = availableEvolutionVariables.find(
          (av) => av.id === slug || av.slug === slug,
        );
        return v?.units ?? null;
      };
      const newVarUnits = getUnits(added[0]);
      const currentUnits =
        prev.length > 0 ? getUnits(prev[0]) : null;
      if (newVarUnits !== currentUnits) {
        const sameUnits = newSelection.filter(
          (s) => getUnits(s) === newVarUnits,
        );
        setSelectedEvolutionVariables(sameUnits);
      } else {
        setSelectedEvolutionVariables(newSelection);
      }
    },
    [selectedEvolutionVariables, availableEvolutionVariables],
  );

  const formatVariableName = React.useCallback((slug: string) => {
    return slug
      .replace(/_/g, " ")
      .replace(/\b\w/g, (letter) => letter.toUpperCase());
  }, []);

  const evolutionVariableKeys = React.useMemo(() => {
    return selectedEvolutionVariables.filter((key) =>
      evolutionAvailableVariables.includes(key),
    );
  }, [selectedEvolutionVariables, evolutionAvailableVariables]);

  const evolutionLineLabels = React.useMemo(() => {
    return evolutionVariableKeys.map((key) => {
      const variable = stormImpactModel?.variables?.find(
        (item) => item.variable_slug === key,
      );
      return variable?.variable_name || formatVariableName(key);
    });
  }, [evolutionVariableKeys, stormImpactModel?.variables, formatVariableName]);

  const evolutionLines = React.useMemo(() => {
    if (evolutionVariableKeys.length === 0) return [];
    return createEvolutionLineConfigs(evolutionVariableKeys, evolutionLineLabels);
  }, [evolutionVariableKeys, evolutionLineLabels]);

  const evolutionYAxisUnits = React.useMemo(() => {
    if (evolutionVariableKeys.length === 0) return units;
    const firstVar = stormImpactModel?.variables?.find((v) =>
      evolutionVariableKeys.includes(v.variable_slug),
    );
    return firstVar?.variable_units || units;
  }, [evolutionVariableKeys, stormImpactModel?.variables, units]);

  const displayTitle = React.useMemo(() => {
    const dayCount =
      data.length > 0 && data.length < forecastRange
        ? data.length
        : forecastRange;
    const dayLabel = dayCount === 1 ? "day" : "days";
    return `${title} (${dayCount} ${dayLabel})`;
  }, [title, forecastRange, data.length]);

  // Build subtitle with date range info (must be before any conditional returns)
  const displaySubtitle = React.useMemo(() => {
    if (subtitle) return subtitle;
    if (data.length > 0) {
      return `${data.length} days`;
    }
    return undefined;
  }, [subtitle, data.length]);

  if (!visible || (!panelMode && waitingForParams)) return null;

  return (
    <AbstractInset
      data={waitingForParams ? [] : data}
      loading={isRefreshing}
      error={!isRefreshing && error ? String(error) : null}
      position={position}
      width={panelMode ? "100%" : width}
      height={panelMode ? "100%" : height}
      title={displayTitle}
      subtitle={displaySubtitle}
      isCollapsible={isCollapsible}
      initiallyCollapsed={initiallyCollapsed}
      className={className}
      containerId="forecast-timeline-inset-container"
      isDraggable={isDraggable}
      onPositionChange={onPositionChange}
      panelMode={panelMode}
      infoTooltip={
        <div className="text-sm space-y-2">
          <p className="font-medium">How to read this panel</p>
          <ul className="text-xs space-y-1 list-disc pl-4">
            <li>
              Timeline shows how forecast values evolve over the next several
              days
            </li>
            <li>
              Evolution shows how forecasts change across model runs for the
              same forecast date
            </li>
            <li>The dashed line shows the best estimate</li>
            <li>
              Weather threat icons indicate predicted significant weather events
            </li>
          </ul>
        </div>
      }
    >
      {(_insetState: InsetState) => (
        <Tabs
          value={activeTab}
          onValueChange={(value) => setActiveTab(value as "timeline" | "evolution")}
          className="flex h-full min-h-0 flex-col overflow-hidden"
        >
          <TabsList className="mx-3 mt-2 grid w-auto grid-cols-2 h-10">
            <TabsTrigger value="timeline" className="text-xs flex items-center gap-1.5 h-7">
              <AreaChart className="h-3.5 w-3.5" />
              Timeline
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span
                      onClick={(e) => e.stopPropagation()}
                      className="flex cursor-help"
                    >
                      <Info className="h-3.5 w-3.5 text-gray-400 hover:text-gray-500" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs">
                    <p className="text-sm">
                      Timeline shows how forecast values evolve over the next
                      several days.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </TabsTrigger>
            <TabsTrigger value="evolution" className="text-xs flex items-center gap-1.5 h-7">
              <GitBranch className="h-3.5 w-3.5" />
              Evolution
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span
                      onClick={(e) => e.stopPropagation()}
                      className="flex cursor-help"
                    >
                      <Info className="h-3.5 w-3.5 text-gray-400 hover:text-gray-500" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs">
                    <p className="text-sm">
                      This chart shows how the forecast has evolved across
                      different model runs for the selected date. Each point
                      represents a different model run time, allowing you to see
                      forecast stability and changes in predictions over time.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </TabsTrigger>
          </TabsList>

          <TabsContent
            value="timeline"
            className={`relative mt-0 flex flex-1 flex-col overflow-hidden data-[state=inactive]:hidden ${panelMode ? "min-h-0" : "min-h-[340px]"}`}
          >
            {(isRefreshing || waitingForParams) && (
              <div className="absolute inset-0 z-10 flex flex-col items-center justify-center p-8 text-center bg-background/80 backdrop-blur-[1px]">
                <div className="flex items-center justify-center space-x-2">
                  <div className="w-4 h-4 rounded-full bg-blue-500 animate-pulse" />
                  <div className="w-4 h-4 rounded-full bg-blue-500 animate-pulse animation-delay-200" />
                  <div className="w-4 h-4 rounded-full bg-blue-500 animate-pulse animation-delay-400" />
                </div>
                <p className="mt-4 text-sm text-muted-foreground">
                  Loading forecast timeline...
                </p>
              </div>
            )}

            {!isRefreshing && !waitingForParams && (error || data.length === 0) && (
              <div className="p-8 text-center">
                <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                  <span className="text-xl">📊</span>
                </div>
                <h3 className="text-sm font-medium mb-2">
                  No Forecast Data Available
                </h3>
                <p className="text-xs text-muted-foreground">
                  No forecast data found for the selected date range.
                </p>
              </div>
            )}

            {!isPlaceholderData && data.length > 0 && (
              <div
                className={`flex h-full min-h-0 flex-1 flex-col p-3 transition-opacity duration-150 ${isRefreshing ? "opacity-0" : "opacity-100"}`}
                aria-hidden={isRefreshing}
              >
                <ForecastTimelineChart
                  data={data}
                  height={panelMode ? "100%" : 280}
                  yAxisLabel={units}
                  showGrid={true}
                  showTooltip={true}
                  showThreats={true}
                  className="w-full min-h-0 flex-1"
                />
              </div>
            )}
          </TabsContent>

          <TabsContent
            value="evolution"
            className={`relative mt-0 flex flex-1 flex-col overflow-hidden data-[state=inactive]:hidden ${panelMode ? "min-h-0" : "min-h-[340px]"}`}
          >
            {isEvolutionRefreshing && (
              <div className="absolute inset-0 z-10 flex flex-col items-center justify-center p-8 text-center bg-background/80 backdrop-blur-[1px]">
                <div className="flex items-center justify-center space-x-2">
                  <div className="w-4 h-4 rounded-full bg-blue-500 animate-pulse" />
                  <div className="w-4 h-4 rounded-full bg-blue-500 animate-pulse animation-delay-200" />
                  <div className="w-4 h-4 rounded-full bg-blue-500 animate-pulse animation-delay-400" />
                </div>
                <p className="mt-4 text-sm text-muted-foreground">
                  Loading forecast evolution...
                </p>
              </div>
            )}

            {!isEvolutionRefreshing && evolutionError && (
              <div className="p-8 text-center">
                <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                  <span className="text-xl">⚠️</span>
                </div>
                <h3 className="text-sm font-medium mb-2">
                  Forecast Evolution Unavailable
                </h3>
                <p className="text-xs text-muted-foreground">
                  Could not load forecast runs for this date. Please retry.
                </p>
              </div>
            )}

            {!isEvolutionRefreshing &&
              !evolutionError &&
              evolutionData.length === 0 && (
                <div className="p-8 text-center">
                  <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                    <span className="text-xl">📈</span>
                  </div>
                  <h3 className="text-sm font-medium mb-2">No Trend Data Available</h3>
                  <p className="text-xs text-muted-foreground">
                    No forecast trend data found for different model runs.
                  </p>
                </div>
              )}

            {!isEvolutionRefreshing &&
              !evolutionError &&
              evolutionData.length > 0 &&
              evolutionVariableKeys.length === 0 && (
                <div className="p-8 text-center">
                  <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                    <span className="text-xl">📊</span>
                  </div>
                  <h3 className="text-sm font-medium mb-2">No Variables Selected</h3>
                  <p className="text-xs text-muted-foreground">
                    Please select at least one variable to display in the chart.
                  </p>
                  <div className="mt-4 flex justify-center">
                    <VariableSelectionDropdown
                      availableVariables={availableEvolutionVariables}
                      selectedVariables={selectedEvolutionVariables}
                      onSelectedVariablesChange={handleEvolutionVariablesChange}
                      formatVariableName={formatVariableName}
                      label="Variables"
                      defaultVariables={DEFAULT_EVOLUTION_VARIABLES}
                    />
                  </div>
                </div>
              )}

            {!isEvolutionRefreshing &&
              !evolutionError &&
              evolutionData.length > 0 &&
              evolutionLines.length > 0 && (
                <div className="h-full flex flex-col p-3">
                  <div className="flex items-center justify-end gap-2 flex-shrink-0">
                    {availableEvolutionVariables.length > 0 && (
                      <VariableSelectionDropdown
                        availableVariables={availableEvolutionVariables}
                        selectedVariables={selectedEvolutionVariables}
                        onSelectedVariablesChange={handleEvolutionVariablesChange}
                        formatVariableName={formatVariableName}
                        label="Variables"
                        defaultVariables={DEFAULT_EVOLUTION_VARIABLES}
                      />
                    )}
                  </div>
                  <div className="min-h-0 flex-1">
                    <ForecastEvolutionChart
                      data={evolutionData}
                      lines={evolutionLines}
                      height={panelMode ? "100%" : 280}
                      yAxisLabel={evolutionYAxisUnits}
                      showGrid={true}
                      showTooltip={true}
                      className="w-full"
                    />
                  </div>
                </div>
              )}
          </TabsContent>
        </Tabs>
      )}
    </AbstractInset>
  );
};

export { ForecastTimelineInset };
export default ForecastTimelineInset;
